import { ApiResponse } from '../apiResponse';
import { RequestOptions } from '../http/requestBuilder';
import {
  ListMerchantsResponse,
  listMerchantsResponseSchema,
} from '../models/listMerchantsResponse';
import {
  RetrieveMerchantResponse,
  retrieveMerchantResponseSchema,
} from '../models/retrieveMerchantResponse';
import { number, optional, string } from '../schema';
import { BaseApi } from './baseApi';

export class MerchantsApi extends BaseApi {
  /**
   * Returns `Merchant` information for a given access token.
   *
   * If you don't know a `Merchant` ID, you can use this endpoint to retrieve the merchant ID for an
   * access token.
   * You can specify your personal access token to get your own merchant information or specify an OAuth
   * token
   * to get the information for the  merchant that granted you access.
   *
   * If you know the merchant ID, you can also use the [RetrieveMerchant](#endpoint-merchants-
   * retrievemerchant)
   * endpoint to get the merchant information.
   *
   * @param cursor The cursor generated by the previous response.
   * @return Response from the API call
   */
  async listMerchants(
    cursor?: number,
    requestOptions?: RequestOptions
  ): Promise<ApiResponse<ListMerchantsResponse>> {
    const req = this.createRequest('GET', '/v2/merchants');
    const mapped = req.prepareArgs({ cursor: [cursor, optional(number())] });
    req.query('cursor', mapped.cursor);
    return req.callAsJson(listMerchantsResponseSchema, requestOptions);
  }

  /**
   * Retrieve a `Merchant` object for the given `merchant_id`.
   *
   * @param merchantId  The ID of the merchant to retrieve. If the string "me" is supplied as the ID, then
   *                              retrieve the merchant that is currently accessible to this call.
   * @return Response from the API call
   */
  async retrieveMerchant(
    merchantId: string,
    requestOptions?: RequestOptions
  ): Promise<ApiResponse<RetrieveMerchantResponse>> {
    const req = this.createRequest('GET');
    const mapped = req.prepareArgs({ merchantId: [merchantId, string()] });
    req.appendTemplatePath`/v2/merchants/${mapped.merchantId}`;
    return req.callAsJson(retrieveMerchantResponseSchema, requestOptions);
  }
}
